import React, { useCallback, useState } from 'react'
import { formatCents, useJsonApi } from 'shared'
import { Modal, PopoverIcon } from 'shared'
import { CompletedNeedsProps, ShowModalLinkProps, ShowMoreNoticeProps, Need, NeedsApiResult } from './types'

/**
 * @func CompletedNeeds
 * @param {CompletedNeedsProps} props
 * @returns {JSX.Element}
 */
export const CompletedNeeds = ({ apiUrl }: CompletedNeedsProps): JSX.Element => {
  const [showModal, setShowModal] = useState(false)
  const hideModal = () => setShowModal(false)
  const { data } = useJsonApi<NeedsApiResult>(apiUrl)
  const needs = (data && data.data) || []
  const count = (data && data.total_entries) || 0

  return (
    <>
      <div style={{ textAlign: 'center' }}>
        <ShowModalLink count={count} setShowModal={setShowModal} />
      </div>
      <Modal
        id="completed-needs-modal"
        onHide={hideModal}
        show={showModal}
        header={I18n.t('projects.show.completed_needs.headline')}
        footer={
          <button type="button" className="btn btn-default" onClick={hideModal} aria-hidden="true">
            {I18n.t('projects.show.completed_needs.close_link_text')}
          </button>
        }
      >
        <p>
          <strong>{I18n.t('projects.show.completed_needs.thank_you_message')}</strong>
        </p>
        <table className="bpc-table bpc-table--striped">
          <tbody>
            {needs.map((need, i) => (
              <NeedRow {...need} key={i} />
            ))}
          </tbody>
        </table>
        <ShowMoreNotice count={count} />
      </Modal>
    </>
  )
}

const ShowModalLink = ({ count, setShowModal }: ShowModalLinkProps): JSX.Element | null => {
  const onClick = useCallback(() => setShowModal(true), [setShowModal])
  if (!count) return null
  return (
    <a className="text-gray-700 hover:text-gray-700 font-regular completed-needs-modal-link" onClick={onClick}>
      {I18n.t('projects.show.completed_needs.link_text')}
    </a>
  )
}

const ShowMoreNotice = ({ count }: ShowMoreNoticeProps): JSX.Element | null => {
  const overflow = count - 20
  if (overflow <= 0) return null
  return (
    <p>
      {I18n.t('projects.show.completed_needs.more_completed_needs_notice', {
        more_count: overflow,
      })}
    </p>
  )
}

const NeedRow = (need: Need): JSX.Element => (
  <tr key={need.id} className="generic-popover-constraints">
    <td>
      <span dangerouslySetInnerHTML={{ __html: need.title }} />{' '}
      <PopoverIcon
        popover={`
          <p>${need.description}</p>
          <p><small>${I18n.t('projects.show.completed_needs.created_date_line', {
            date: I18n.l('date.formats.short_date_with_week_day', need.created_at),
          })}</small></p>
        `}
        icon="info"
      />
    </td>
    <td style={{ textAlign: 'right' }}>
      {formatCents(need.requested_amount_in_cents)}&nbsp;
      {I18n.t('projects.show.completed_needs.collected_label')}
    </td>
  </tr>
)
